import { useState } from "react";
import { format } from "date-fns";
import { sv, enUS, type Locale } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { commonDateLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { toInputDate } from "@/utils/dateUtils";

function dateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export type DatePickerProps = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  /** Web calendar only — native uses minDate/maxDate when provided. */
  disabled?: (date: Date) => boolean;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  calendarClassName?: string;
  align?: "start" | "center" | "end";
  locale?: Locale;
  onOpenAutoFocus?: (event: Event) => void;
};

/**
 * Date picker — native OS picker in Capacitor, Popover + Calendar on web.
 */
export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  placeholder,
  className,
  triggerClassName,
  calendarClassName,
  align = "start",
  locale,
  onOpenAutoFocus,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const dateLabels = commonDateLabels(language);
  const dateLocale = locale ?? (language === "en" ? enUS : sv);
  const placeholderText = placeholder ?? dateLabels.selectDate ?? "Select date";

  const handleNativeChange = (dateStr: string) => {
    if (!dateStr) {
      onChange(undefined);
      return;
    }
    const [y, m, d] = dateStr.split("-").map(Number);
    onChange(new Date(y, m - 1, d));
  };

  if (isNative) {
    return (
      <div className={cn("relative", className)}>
        <Input
          type="date"
          value={value ? toInputDate(value) : ""}
          min={minDate ? toInputDate(minDate) : undefined}
          max={maxDate ? toInputDate(maxDate) : undefined}
          onChange={(e) => handleNativeChange(e.target.value)}
          className={cn(
            "pr-9 [&::-webkit-calendar-picker-indicator]:opacity-100",
            triggerClassName,
          )}
        />
        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    );
  }

  const isDayDisabled = (day: Date) => {
    const d = dateOnly(day);
    if (minDate && d < dateOnly(minDate)) return true;
    if (maxDate && d > dateOnly(maxDate)) return true;
    return disabled?.(day) ?? false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            triggerClassName,
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {value ? format(value, "PPP", { locale: dateLocale }) : placeholderText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align={align}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <Calendar
          mode="single"
          selected={value}
          onSelect={(selected) => {
            onChange(selected);
            setOpen(false);
          }}
          initialFocus
          disabled={isDayDisabled}
          className={calendarClassName}
        />
      </PopoverContent>
    </Popover>
  );
}
